<template>
  <div class="whiteboard-host">
    <div
      v-if="reauthenticationRequired"
      class="session-reauth-overlay"
    >
      <form
        class="session-reauth-card"
        @submit.prevent="submitReauthentication"
      >
        <i class="fas fa-shield-alt session-reauth-icon" />
        <h2>Bu cihazdaki oturum değiştirildi</h2>
        <p>
          Oda ve tahta korunuyor. Bu cihazda devam etmek için hesabınıza yeniden
          giriş yapın.
        </p>
        <input
          v-model.trim="reauthEmail"
          type="text"
          autocomplete="username"
          placeholder="E-posta veya telefon"
          required
        >
        <input
          v-model="reauthPassword"
          type="password"
          autocomplete="current-password"
          placeholder="Şifre"
          required
        >
        <div
          v-if="reauthError"
          class="session-reauth-error"
        >
          {{ reauthError }}
        </div>
        <button
          type="submit"
          :disabled="reauthenticating"
        >
          {{ reauthenticating ? "Bağlantı kuruluyor…" : "Bu cihazda devam et" }}
        </button>
        <button
          type="button"
          class="session-reauth-cancel"
          @click="cancelReauthentication"
        >
          Oturumdan çık
        </button>
      </form>
    </div>
    <!-- Dummy-proof reconnect fallback: shown only if auto-recovery can't restore
         the link in time. Reloading re-joins the same persistent room. -->
    <div
      v-if="showReloadPrompt"
      class="reconnect-banner"
    >
      <span><i class="fas fa-triangle-exclamation" /> Bağlantı yeniden
        kurulamadı.</span>
      <button
        class="reconnect-reload-btn"
        @click="reloadPage"
      >
        <i class="fas fa-rotate-right" /> Sayfayı Yenile
      </button>
    </div>
    <!-- Loading Overlay -->
    <transition name="fade-loading">
      <div
        v-if="!hostReady"
        class="loading-overlay"
      >
        <div class="loading-card">
          <div class="loading-spinner">
            <div class="spinner-ring" />
            <i class="fas fa-chalkboard-teacher spinner-icon" />
          </div>
          <h2 class="loading-title">
            Oda Hazırlanıyor
          </h2>
          <p class="loading-subtitle">
            Bağlantılar kuruluyor, lütfen bekleyin...
          </p>
          <div class="loading-steps">
            <div
              class="loading-step"
              :class="{ done: connected }"
            >
              <i
                class="fas"
                :class="connected ? 'fa-check-circle' : 'fa-circle-notch fa-spin'"
              />
              <span>Sunucuya bağlanılıyor</span>
            </div>
            <div
              class="loading-step"
              :class="{ done: roomJoined }"
            >
              <i
                class="fas"
                :class="roomJoined ? 'fa-check-circle' : (connected ? 'fa-circle-notch fa-spin' : 'fa-circle')"
              />
              <span>Oda oluşturuluyor</span>
            </div>
            <div
              class="loading-step"
              :class="{ done: webrtcInitialized }"
            >
              <i
                class="fas"
                :class="webrtcInitialized ? 'fa-check-circle' : (roomJoined ? 'fa-circle-notch fa-spin' : 'fa-circle')"
              />
              <span>WebRTC başlatılıyor</span>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <!-- Left Sidebar (Tools Only) -->
    <div
      class="left-sidebar"
      :class="{
        collapsed: !sidebarVisible,
        'mobile-open': leftSidebarOpen,
        'fullscreen-hidden': isFullscreen,
      }"
      @click.self="leftSidebarOpen && closeMobileSidebars()"
    >
      <!-- Mobile Sidebar Toggle (Only visible on mobile) -->
      <div
        class="mobile-sidebar-toggle mobile-only"
        @click="toggleLeftSidebar"
      >
        <i
          class="fas"
          :class="leftSidebarOpen ? 'fa-times' : 'fa-bars'"
        />
        <span>{{ leftSidebarOpen ? "Kapat" : "Araçlar" }}</span>
      </div>
      <!-- Sidebar Content -->
      <div class="sidebar-content">
        <b-tabs
          class="h-100 d-flex flex-column"
          fill
        >
          <b-tab
            title="Araçlar"
            active
            class="genel-tab-pane"
          >
            <!-- Compact Toolbox -->
            <div class="sidebar-section toolbox-section">
              <Toolbox
                :active-tool="activeTool"
                :can-undo="canUndoState"
                :can-redo="canRedoState"
                @tool-changed="handleToolChange"
                @tool-options-changed="handleToolOptionsChange"
                @clear-canvas="handleClearCanvas"
                @undo-action="handleUndo"
                @redo-action="handleRedo"
              />
            </div>
          </b-tab>

          <b-tab
            title="Egzersiz"
            class="genel-tab-pane"
          >
            <!-- Live Entry Section -->
            <div
              v-if="showLiveEntry && currentExerciseId"
              class="sidebar-section live-entry-section"
            >
              <div class="live-entry-header">
                <h4>Canlı Egzersiz Girişi</h4>
                <button
                  class="close-btn"
                  @click="showLiveEntry = false"
                >
                  <i class="fas fa-times" />
                </button>
              </div>
              <LiveEntry
                :exerc-i-d="currentExerciseId"
                :subj="currentSubject"
                @ExerciseChange="handleExerciseChange"
                @timerStart="handleTimerStart"
                @timerPause="handleTimerPause"
              />
            </div>
            <!-- LiveEntry Toggle Button -->
            <div
              v-if="!showLiveEntry"
              class="sidebar-section"
            >
              <button
                class="action-btn live-entry-toggle"
                @click="toggleLiveEntry"
              >
                <i class="fas fa-stopwatch" /> Canlı Egzersiz Girişi
              </button>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
    <!-- Main Canvas Area -->
    <div class="main-content">
      <CanvasStage
        ref="canvasStage"
        :room-id="roomId"
        :active-tool="activeTool"
        :tool-options="toolOptions"
        :sidebar-visible="sidebarVisible"
        @canvas-updated="handleCanvasUpdate"
        @canvas-state-changed="updateCanvasStates"
        @zoom-updated="handleZoomUpdate"
        @toggle-sidebars="toggleSidebars"
        @toggle-fullscreen="toggleFullscreen"
      />
      <!-- Bottom Floating Controls -->
      <div
        class="bottom-controls"
        :class="{ 'fullscreen-hidden': isFullscreen }"
      >
        <!-- Local Camera (Bottom Left) -->
        <div
          class="bottom-left-controls"
          :class="{ maximize: !gestured }"
        >
          <div
            class="floating-camera local-cam"
            :class="{ hidden: !localCameraVisible }"
            :style="{ display: localCameraVisible ? 'block' : 'none' }"
          >
            <div
              v-if="!gestured"
              class="camera-header"
            >
              <div class="permission-status">
                <div
                  v-if="!hasAudioDevice && !hasVideoDevice"
                  class="no-devices"
                >
                  Kamera veya mikrofon bulunamadı
                </div>
                <div
                  v-else
                  class="device-status"
                >
                  <div
                    v-if="hasAudioDevice"
                    class="audio-status"
                  >
                    <i
                      class="fas fa-microphone"
                      :class="{
                        'text-success': audioPermissionGranted,
                        'text-danger': audioPermissionDenied,
                        'text-muted': !audioPermissionGranted && !audioPermissionDenied,
                      }"
                    />
                    <span v-if="audioPermissionDenied">Mikrofon reddedildi</span>
                    <span v-else-if="audioPermissionGranted">Mikrofon hazır</span>
                    <span v-else>Mikrofon mevcut</span>
                  </div>
                  <div
                    v-if="hasVideoDevice"
                    class="video-status"
                  >
                    <i
                      class="fas fa-video"
                      :class="{
                        'text-success': videoPermissionGranted,
                        'text-danger': videoPermissionDenied,
                        'text-muted': !videoPermissionGranted && !videoPermissionDenied,
                      }"
                    />
                    <span v-if="videoPermissionDenied">Kamera reddedildi</span>
                    <span v-else-if="videoPermissionGranted">Kamera hazır</span>
                    <span v-else>Kamera mevcut</span>
                  </div>
                </div>
              </div>
              <button
                class="btn"
                :class="{
                  'btn-primary': audioPermissionGranted || videoPermissionGranted,
                  'btn-secondary': !audioPermissionGranted && !videoPermissionGranted,
                }"
                :disabled="!hasAudioDevice && !hasVideoDevice"
                @click="
                  audioPermissionGranted || videoPermissionGranted
                    ? handleGesture(true)
                    : handleGesture(false)
                "
              >
                {{
                  audioPermissionGranted || videoPermissionGranted
                    ? "Hazır Olduğunda Buraya Tıkla"
                    : "İzinleri Al"
                }}
              </button>
            </div>
            <div
              v-else
              class="camera-header"
            >
              <span>Senin Kameran</span>
              <button
                class="hide-btn"
                @click="localCameraVisible = false"
              >
                <i class="fas fa-times" />
              </button>
            </div>

            <LocalCamera
              ref="localCamera"
              @mic-toggle="handleMicToggle"
              @camera-toggle="handleCameraToggle"
            >
              <template #video>
                <video
                  ref="vp"
                  style="height: 100%; width: 100%"
                  autoplay
                  playsinline
                  :muted="gestured || micMuted"
                />
              </template>
            </LocalCamera>
          </div>
          <!-- Local Camera Toggle Button -->
          <button
            v-if="!localCameraVisible"
            class="camera-toggle-btn local"
            @click="localCameraVisible = !localCameraVisible"
          >
            <i class="fas fa-video" />
          </button>
        </div>
        <!-- Guest Camera (Bottom Right, below upload controls) -->
        <div class="bottom-right-controls">
          <div
            class="floating-camera guest-cam"
            :class="{ hidden: !guestCameraVisible }"
            :style="{ display: guestCameraVisible ? 'block' : 'none' }"
          >
            <div class="camera-header">
              <span>Misafir Kamerası</span>
              <button
                class="hide-btn"
                @click="guestCameraVisible = false"
              >
                <i class="fas fa-times" />
              </button>
            </div>
            <GuestCamera
              :guest-online="guestConnected"
              :guest-name="guestName"
            >
              <template #video>
                <video
                  ref="vpremote"
                  style="height: 100%; width: 100%"
                  autoplay
                  playsinline
                  :muted="remoteMuted"
                />
                <div
                  v-if="remoteMuted"
                  class="mic-off-indicator"
                >
                  <i class="fas fa-microphone-slash mic-off-icon" />
                  <span class="mic-off-text">Mikrofon Kapalı</span>
                </div>
              </template>
            </GuestCamera>
          </div>
          <!-- Guest Camera Toggle Button -->
          <button
            v-if="!guestCameraVisible"
            class="camera-toggle-btn guest"
            @click="guestCameraVisible = !guestCameraVisible"
          >
            <i class="fas fa-user" />
          </button>
        </div>
      </div>
    </div>
    <!-- Right Sidebar (Chat & Permissions Only) -->
    <div
      class="right-sidebar"
      :class="{
        collapsed: !sidebarVisible,
        'mobile-open': rightSidebarOpen,
        'fullscreen-hidden': isFullscreen,
      }"
      @click.self="rightSidebarOpen && closeMobileSidebars()"
    >
      <!-- Mobile Sidebar Toggle (Only visible on mobile) -->
      <div
        class="mobile-sidebar-toggle mobile-only"
        @click="toggleRightSidebar"
      >
        <i
          class="fas"
          :class="rightSidebarOpen ? 'fa-times' : 'fa-bars'"
        />
        <span>{{ rightSidebarOpen ? "Close" : "Chat" }}</span>
      </div>
      <!-- Sidebar Content -->
      <div class="sidebar-content">
        <b-tabs
          class="h-100 d-flex flex-column"
          fill
        >
          <b-tab
            title="Genel"
            active
            class="genel-tab-pane"
          >
            <!-- Compact Permission Controls -->
            <div class="sidebar-section permission-controls compact">
              <PermissionControls
                ref="permissionControls"
                @permissions-changed="handlePermissionsChanged"
              />
            </div>
            <!-- Room ID Display -->
            <div
              v-if="roomId"
              class="sidebar-section room-info"
            >
              <div class="room-id-container">
                <!-- <div class="room-header">
                  <h5>Oturum Oda ID'si</h5>
                  <button @click="copyRoomId" class="copy-btn" title="Copy Room ID">
                    <i class="fas fa-copy"></i>
                  </button>
                </div> -->
                <!-- <div class="room-id-display">
                  <code>{{ roomId }}</code>
                </div> -->
                <!-- <div class="guest-url">
                   <small>Misafir URL'si:</small>
                  <button @click="copyGuestUrl" class="copy-url-btn" title="Copy Guest URL">
                    <i class="fas fa-link"></i>
                  </button>
                </div> -->
                <b-button
                  variant="info"
                  @click="copyGuestUrl"
                >
                  Misafir Bağlantı Linkini Kopyala
                </b-button>
                <b-button
                  variant="danger"
                  class="mt-2"
                  @click="terminateSession"
                >
                  <i class="fas fa-power-off" /> Oturumu Sonlandır
                </b-button>
              </div>
            </div>
            <!-- Automatic WebRTC Connection Status -->
            <div class="sidebar-section connection-status">
              <div class="connection-container">
                <div class="connection-header">
                  <h5>Doğrudan Bağlantı Durumu</h5>
                </div>
                <div class="connection-info">
                  <div
                    class="connection-status"
                    :class="{ connected: webrtcConnected }"
                  >
                    <i class="fas fa-circle" />
                    {{
                      webrtcConnected
                        ? "Misafir Bağlandı"
                        : guestReconnecting
                          ? "Yeniden bağlanılıyor…"
                          : "Misafir Bekleniyor..."
                    }}
                  </div>
                  <!-- <small v-if="!webrtcConnected" style="color: #666; font-size: 11px">
                    Bağlantı, misafir katıldığında otomatik olarak kurulacaktır
                  </small> -->
                </div>
              </div>
            </div>
          </b-tab>
          <b-tab
            title="Mesajlar"
            class="chat-tab-pane"
          >
            <!-- Chat Box -->
            <div class="sidebar-section chat-box border-0 p-0">
              <div class="chat-container">
                <div class="chat-header">
                  <h4>Chat</h4>
                  <span
                    class="connection-status"
                    :class="{ connected: connected }"
                  >
                    <i class="fas fa-circle" />
                    {{ connected ? "Bağlandı" : "Bağlantı Kesildi" }}
                  </span>
                </div>
                <div
                  ref="chatMessages"
                  class="chat-messages"
                >
                  <div
                    v-for="message in messages"
                    :key="message.id"
                    class="message"
                    :class="{
                      system: message.from === 'system',
                      own: message.from === 'host',
                    }"
                  >
                    <div class="message-content">
                      {{ message.content }}
                    </div>
                    <div class="message-time">
                      {{ formatTime(message.timestamp) }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="connected || webrtcConnected"
                  class="chat-input"
                >
                  <input
                    v-model="newMessage"
                    placeholder="Mesaj Yaz..."
                    class="message-input"
                    @keyup.enter="sendMessage"
                  >
                  <button
                    class="send-btn"
                    @click="sendMessage"
                  >
                    <i class="fas fa-paper-plane" />
                  </button>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-alert, no-await-in-loop, no-plusplus, no-underscore-dangle */
import { BButton, BTabs, BTab } from 'bootstrap-vue'
import io from 'socket.io-client'
import axios from '@axios'
import useJwt from '@/auth/jwt/useJwt'
import createSocketReissueCoordinator from '@/auth/socketReissue'
import {
  setPhoneSetupHandoff,
  SOURCE_LOGIN_MODERN,
} from '@/auth/phoneSetupHandoff'
import {
  guardSubscriptionAction,
  ActionRequirement,
} from '@/libs/subscriptionActionGuard'
import Toolbox from './components/Toolbox.vue'
import LocalCamera from './components/LocalCamera.vue'
import CanvasStage from './components/CanvasStage.vue'
import GuestCamera from './components/GuestCamera.vue'
import PermissionControls from './components/PermissionControls.vue'
import LiveEntry from './LiveEntry.vue'
import WebRTCService from './services/WebRTCService'
import useCamera from './mixins/useCamera'

const {
  axiosRoutes: { online: path },
} = require('../../../../../G_CONFIG')

export default {
  name: 'HostView',
  components: {
    Toolbox,
    LocalCamera,
    CanvasStage,
    GuestCamera,
    PermissionControls,
    LiveEntry,
    BButton,
    BTabs,
    BTab,
  },
  mixins: [useCamera],
  data() {
    return {
      // Sidebar state
      gestured: false,
      sidebarVisible: true,
      leftSidebarOpen: false,
      rightSidebarOpen: false,
      isFullscreen: false,

      // Floating camera state
      localCameraVisible: true,
      guestCameraVisible: true,

      // Socket connection (only for chat and basic room management)
      socket: null,
      socketReissueCoordinator: null,
      unsubscribeTokenReissued: null,
      connected: false,
      roomId: null,
      // P2-9 — signed room-scoped guest invite token, embedded in guestUrl
      guestToken: null,

      // WebRTC for direct P2P communication
      webrtc: null,
      webrtcConnected: false,
      guestConnected: false,
      guestReconnecting: false,
      showReloadPrompt: false,
      reauthenticationRequired: false,
      reauthenticating: false,
      reauthEmail: '',
      reauthPassword: '',
      reauthError: '',
      reauthRemembrance: false,
      guestName: 'Guest User',
      // Loading state
      hostReady: false,
      roomJoined: false,
      webrtcInitialized: false,
      // Guest Permissions Management
      currentGuestPermissions: {
        canDraw: true,
        canErase: true,
        canAddShapes: true,
        canUploadBackground: false,
        canClear: false,
        canSelect: false,
        canDragCanvas: false,
      },

      // Messages
      messages: [],
      newMessage: '',

      // LiveEntry data
      currentExerciseId: null,
      currentSubject: null,
      showLiveEntry: false,
      exerciseImageRequests: new Map(),

      activeTool: 'select',
      canUndoState: false,
      canRedoState: false,
      toolOptions: {
        select: {},
        pen: {
          strokeWidth: 10,
          stroke: '#000000',
        },
        eraser: {
          strokeWidth: 10,
        },
        rectangle: {
          strokeWidth: 10,
          stroke: '#000000',
          fill: 'transparent',
          filled: false,
        },
        circle: {
          strokeWidth: 10,
          stroke: '#000000',
          fill: 'transparent',
          filled: false,
        },
      },
    }
  },
  computed: {
    guestUrl() {
      if (this.roomId && this.$route.params.subjectid) {
        const base = `${window.location.origin}/online/subject/${this.$route.params.subjectid}/${this.roomId}`
        // P2-9 — carry the guest invite token so the guest's socket can authenticate.
        return this.guestToken
          ? `${base}?t=${encodeURIComponent(this.guestToken)}`
          : base
      }
      return ''
    },

  },
  async created() {
    this.roomId = this.$route.params.roomid
    const resp = await axios.get(
      `${path.get}/${this.$route.params.subjectid}/${this.roomId}`,
    )
    console.log(resp.data)
    this.currentExerciseId = resp.data.exercise
    this.guestName = resp.data.subject.name
    // P2-9 — mint a room-scoped guest invite token (host-only endpoint) so the
    // shareable guest URL carries it; the guest's socket handshake needs it.
    try {
      const { data } = await axios.get(`${path.get}/${this.roomId}/guest-token`)
      this.guestToken = data.token
    } catch (e) {
      console.error('Failed to mint guest invite token:', e)
    }
    this.LoadExerciseImage()
  },
  async mounted() {
    // add local audio and video tracks

    // Add window resize listener for responsive canvas
    window.addEventListener('resize', this.handleResize)
    // Add keydown listener for closing mobile sidebars
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('diko:session-replaced', this.handleSessionReplacement)

    // Initialize socket connection
    this.initializeSocket()

    // Initialize undo/redo states after component is mounted
    this.$nextTick(() => {
      this.updateCanvasStates()
    })

    // Check devices on mount
    await this.checkAvailableDevices()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('diko:session-replaced', this.handleSessionReplacement)
    if (this.unsubscribeTokenReissued) {
      this.unsubscribeTokenReissued()
      this.unsubscribeTokenReissued = null
    }
    this.disarmReloadPrompt()

    // Disconnect WebRTC
    if (this.webrtc) {
      this.webrtc.disconnect()
    }

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect()
    }

    // Clean up media streams
    this.cleanupStreams()
  },
  methods: {
    // P4-16 — resilient TURN credential fetch with exponential backoff. Returns the
    // iceServers array on success, or null after exhausting retries (caller falls
    // back to the service's default STUN). Mirrors GuestView.fetchTurnCredentials.
    async fetchTurnCredentials(attempts = 3) {
      for (let i = 0; i < attempts; i++) {
        try {
          const { data } = await axios.get('/online/turn-credentials', {
            params: { roomId: this.roomId },
          })
          if (data && data.iceServers) return data.iceServers
          return null
        } catch (err) {
          const status = err.response && err.response.status
          console.warn(
            `TURN credential fetch failed (attempt ${i + 1}/${attempts})`,
            status || err.message,
          )
          if (i < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 500 * 2 ** i))
          }
        }
      }
      return null
    },

    // Modified handleGesture method
    async handleGesture(ready = false) {
      try {
        if (
          !this.videoPermissionGranted
          && !this.audioPermissionGranted
          && (this.hasAudioDevice || this.hasVideoDevice)
        ) {
          // Check available devices first

          // Request permissions separately
          /*  const audioGranted = */ await this.requestAudioPermission()
          /*  const videoGranted =  */ await this.requestVideoPermission()

          // Create combined stream for WebRTC
          const combined = this.combineStreams()

          if (combined) {
            this.localStream = combined
          }
        }

        if (ready && this.webrtc && this.localStream) {
          this.gestured = true
          this.webrtc.addTracks(this.localStream)
        }
      } catch (err) {
        console.error('Error in handleGesture:', err)
      }
    },

    // Existing methods
    handleToolChange(tool) {
      this.activeTool = tool
    },
    handleToolOptionsChange(tool, options) {
      this.toolOptions[tool] = { ...this.toolOptions[tool], ...options }
    },
    handleUndo() {
      // Call undo method on canvas component
      if (this.$refs.canvasStage) {
        this.$refs.canvasStage.undo()
        this.updateCanvasStates()
      }
    },
    handleRedo() {
      // Call redo method on canvas component
      if (this.$refs.canvasStage) {
        this.$refs.canvasStage.redo()
        this.updateCanvasStates()
      }
    },
    updateCanvasStates() {
      // Update undo/redo state properties
      if (this.$refs.canvasStage) {
        this.canUndoState = this.$refs.canvasStage.canUndo()
        this.canRedoState = this.$refs.canvasStage.canRedo()
      }
    },
    handleCanvasUpdate(data) {
      // P1-5 — whiteboard sync now rides Socket.IO. Lightweight ops (strokes,
      // shapes, clear, undo/redo of shapes) go over the reliable server relay;
      // the heavy base64 background still uses the WebRTC data channel until
      // P1-7 replaces it with a server URL (a `data:`/chunk payload marks the
      // heavy path, so it stays on the data channel).
      const bg = data.backgroundImage
      const heavy = (typeof bg === 'string' && bg.startsWith('data:')) || (bg && bg.chunk)
      if (heavy) {
        if (this.webrtc && this.webrtcConnected) {
          this.webrtc.sendCanvasUpdate(data)
        }
      } else if (this.socket && this.roomId) {
        this.socket.emit('canvas-op', this.roomId, 'host', data)
      }

      // Update undo/redo button states after canvas changes
      this.updateCanvasStates()
    },

    handleZoomUpdate(zoomData) {
      // Send zoom updates directly to connected guests via WebRTC
      if (this.webrtc && this.webrtcConnected) {
        console.log('📤 Broadcasting zoom update via WebRTC:', zoomData)
        this.webrtc.sendZoomUpdate(zoomData)
      } else {
        console.log('⚠️ WebRTC not connected, zoom update not sent')
      }
    },

    handlePermissionsChanged(newPermissions) {
      this.currentGuestPermissions = { ...newPermissions }

      // Send permission updates to connected guest via WebRTC
      if (this.webrtc && this.webrtcConnected) {
        console.log('📤 Broadcasting permission update via WebRTC:', newPermissions)
        this.webrtc.sendPermissionUpdate(newPermissions)
      } else {
        console.log('⚠️ WebRTC not connected, permission update not sent')
      }
    },

    handleClearCanvas() {
      if (this.$refs.canvasStage) {
        this.$refs.canvasStage.clearCanvas()
      }
    },

    // LiveEntry event handlers
    handleExerciseChange(exerciseId) {
      console.log('Exercise changed to:', exerciseId)
      this.currentExerciseId = exerciseId
      this.LoadExerciseImage()
    },

    handleTimerStart() {
      console.log('Timer started')
      // You can add additional logic here if needed
    },

    handleTimerPause() {
      console.log('Timer paused')
      // You can add additional logic here if needed
    },

    toggleLiveEntry() {
      if (!this.currentSubject) {
        // Initialize with subject from route
        this.currentSubject = this.$route.params.subjectid
      }

      if (!this.currentExerciseId) {
        // Prompt user for exercise ID or get from route/query
        const exerciseId = this.$route.params.exerciseid
          || this.$route.query.exerciseId
          || prompt('Enter Exercise ID:')
        if (exerciseId) {
          this.currentExerciseId = exerciseId
          this.showLiveEntry = true
        }
      } else {
        this.showLiveEntry = !this.showLiveEntry
      }
    },

    toggleSidebars() {
      this.sidebarVisible = !this.sidebarVisible
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      // Hide all sidebars when in fullscreen mode
      if (this.isFullscreen) {
        this.sidebarVisible = false
        this.leftSidebarOpen = false
        this.rightSidebarOpen = false
        this.localCameraVisible = false
        this.guestCameraVisible = false
      } else {
        // Restore normal view
        this.sidebarVisible = true
        this.localCameraVisible = true
        this.guestCameraVisible = true
      }
    },
    toggleLeftSidebar() {
      this.leftSidebarOpen = !this.leftSidebarOpen
      // Close right sidebar if it's open to prevent overlap
      if (this.leftSidebarOpen && this.rightSidebarOpen) {
        this.rightSidebarOpen = false
      }
    },
    toggleRightSidebar() {
      this.rightSidebarOpen = !this.rightSidebarOpen
      // Close left sidebar if it's open to prevent overlap
      if (this.rightSidebarOpen && this.leftSidebarOpen) {
        this.leftSidebarOpen = false
      }
    },
    closeMobileSidebars() {
      this.leftSidebarOpen = false
      this.rightSidebarOpen = false
    },
    handleKeydown(event) {
      // Close mobile sidebars on Escape key
      if (event.key === 'Escape') {
        this.closeMobileSidebars()
      }
    },
    handleResize() {
      // Force canvas to re-render on window resize
      this.$nextTick(() => {
        if (this.$refs.canvasStage) {
          this.$refs.canvasStage.recalculateStage()
        }
      })
    },

    // P2-9 — the host's socket handshake was rejected (expired/invalid access
    // token, or it is not this room's host). There is no socket token-refresh,
    // so re-login is the only recovery. Tear down and route to login rather than
    // leaving a silently broken board.
    // Dummy-proof fallback: if the connection stays down past the grace/auto-
    // recovery window, surface a single obvious "reload the page" action. A fresh
    // page load re-auths and rejoins the SAME persistent room (Mongo-backed),
    // restoring the whiteboard and re-establishing media — no other steps needed.
    armReloadPrompt() {
      if (this._reloadPromptTimer || this.showReloadPrompt) return
      this._reloadPromptTimer = setTimeout(() => {
        this._reloadPromptTimer = null
        this.showReloadPrompt = true
      }, 20000)
    },
    disarmReloadPrompt() {
      if (this._reloadPromptTimer) {
        clearTimeout(this._reloadPromptTimer)
        this._reloadPromptTimer = null
      }
      this.showReloadPrompt = false
    },
    reloadPage() {
      window.location.reload()
    },

    beginReauthentication(payload = {}) {
      if (this.reauthenticationRequired) return
      this.reauthRemembrance = Boolean(
        useJwt.session && useJwt.session.persistent,
      )
      this.reauthenticationRequired = true
      this.reauthError = ''
      this.connected = false
      this.roomJoined = false
      this.webrtcConnected = false
      this.guestConnected = false
      this.gestured = false
      try {
        if (this.socket) this.socket.disconnect()
        if (this.webrtc) this.webrtc.disconnect()
        this.cleanupStreams()
      } catch (error) {
        console.log('⚠️ Error during auth-failure cleanup:', error)
      }
      this.socket = null
      this.webrtc = null
      this.localStream = null
      this.remoteStream = null
      this.messages.push({
        id: `auth-${Date.now()}`,
        from: 'system',
        content: payload.reason === 'device_slot_replaced'
          ? 'Bu cihaz oturumu başka bir giriş tarafından değiştirildi.'
          : 'Oturum doğrulaması yenilenmeli.',
        timestamp: new Date(),
      })
    },

    handleSessionReplacement(eventOrPayload = {}) {
      const payload = eventOrPayload.detail || eventOrPayload
      if (this.reauthenticationRequired) return
      const persistent = Boolean(useJwt.session && useJwt.session.persistent)
      this.beginReauthentication(payload)
      this.reauthRemembrance = persistent
      useJwt.handleSessionReplaced(payload)
    },

    handleAuthFailure() {
      if (this._authFailed) return
      this._authFailed = true
      this.beginReauthentication({ reason: 'session_expired' })
    },

    async submitReauthentication() {
      if (this.reauthenticating) return
      this.reauthenticating = true
      this.reauthError = ''
      try {
        const loginPayload = {
          identifier: this.reauthEmail,
          password: this.reauthPassword,
          remembrance: this.reauthRemembrance,
        }
        const intent = await useJwt.beginLoginIntent()
        const prepared = await useJwt.prepareLogin(loginPayload, intent)
        if (prepared.data && prepared.data.phoneSetupRequired) {
          // CODE-R4-003: durably promote the shared journal to the
          // approved phone-setup phase before storing the module-local
          // handoff or navigating.
          await useJwt.promotePhoneSetupIntent(intent.identity, prepared.data.phoneSetupToken)
          setPhoneSetupHandoff({
            token: prepared.data.phoneSetupToken,
            tokenExpiresAt:
              Date.now() + (prepared.data.phoneSetupExpiresInSeconds || 600) * 1000,
            source: SOURCE_LOGIN_MODERN,
            loginIntent: intent,
            returnTo: this.$route.fullPath,
          })
          this.reauthenticationRequired = false
          await this.$router.push({ name: 'phone-setup' })
          return
        }
        const response = await useJwt.finalizeLogin({
          intent,
          loginGrant: prepared.data && prepared.data.loginGrant,
          loginPayload,
        })
        if (response.data.isParent) throw new Error('parent_not_supported')
        this.reauthenticationRequired = false
        this.reauthPassword = ''
        this._authFailed = false
        this._triedTokenRefresh = false
        this.hostReady = false
        await this.initializeSocket()
      } catch (error) {
        this.reauthError = 'Giriş başarısız. Kimlik ve şifrenizi kontrol edin.'
      } finally {
        this.reauthenticating = false
      }
    },

    cancelReauthentication() {
      this.reauthenticationRequired = false
      this.$router.push({
        name: 'auth-login',
        query: { redirect: this.$route.fullPath },
      })
    },

    async LoadExerciseImage() {
      const exerciseId = this.currentExerciseId
      const existingRequest = this.exerciseImageRequests.get(exerciseId)
      if (existingRequest) return existingRequest

      const request = (async () => {
        const decision = await guardSubscriptionAction({
          requirement: ActionRequirement.SUBSCRIPTION_ONLY,
          source: 'online-host-exercise-reprint',
        })
        if (!decision.allowed) return

        const resp = await axios.post(
          `/${`exercises/reprint/${this.$route.params.subjectid}?id=${exerciseId}`}`,
        )
        // A newer exercise may finish first; never paint an older response over it.
        if (this.currentExerciseId !== exerciseId) return
        const exerciseImage = `${axios.defaults.baseURL}/images/${resp.data[0].question}.jpg`
        this.$refs.canvasStage.HandleURLImage(exerciseImage)
      })()

      this.exerciseImageRequests.set(exerciseId, request)
      try {
        return await request
      } finally {
        if (this.exerciseImageRequests.get(exerciseId) === request) {
          this.exerciseImageRequests.delete(exerciseId)
        }
      }
    },
    // Socket connection methods
    async initializeSocket() {
      try {
        try {
          await useJwt.ensureFreshAccessToken(30)
        } catch (error) {
          const data = error && error.response && error.response.data
          if (data && data.code === 'SESSION_REPLACED') {
            this.handleSessionReplacement(data)
          } else {
            this.handleAuthFailure()
          }
          return
        }
        // Connect to socket server
        // P2-9 — authenticate the socket handshake with the host's user JWT.
        this.socket = io(axios.defaults.baseURL.replace('/api', ''), {
          transports: ['websocket', 'polling'],
          // Function form: re-read the CURRENT access token on every (re)handshake.
          // A static object would replay the token captured at socket creation, so
          // a token refreshed mid-lesson wouldn't reach a later reconnect handshake.
          auth: cb => cb({ token: useJwt.getToken() }),
          // Keep retrying forever with a bounded backoff so a flaky mobile link
          // recovers on its own instead of stranding the session on one failed try.
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
        })

        if (this.unsubscribeTokenReissued) this.unsubscribeTokenReissued()
        this.socketReissueCoordinator = createSocketReissueCoordinator({
          socket: this.socket,
          jwtService: useJwt,
          onRevoked: payload => this.handleSessionReplacement(payload),
        })
        this.unsubscribeTokenReissued = useJwt.onTokenReissued(
          this.socketReissueCoordinator.onFreshToken,
        )

        this.socket.on('connect', () => {
          console.log('Host connected to socket server')
          this.connected = true
          this.socketReissueCoordinator.onConnect()
          this._triedTokenRefresh = false // allow a fresh refresh on a later expiry
          this.createSession()
        })

        this.socket.on('connected', data => {
          console.log('Server confirmed connection:', data)
        })

        this.socket.on('auth-session-revoked', payload => {
          this.socketReissueCoordinator.onServerRevoked(payload)
        })

        this.socket.on('peer-info', (roomid, who, peerConnection) => {
          console.log(`Peer connected: ${who} in room ${roomid}`)
          console.log('Peer connection:', peerConnection)

          // WebRTC signaling payloads (offer/answer/ICE candidates) are handled by
          // WebRTCService's own peer-info listener. Ignore them here — otherwise
          // each trickled ICE candidate would retrigger offer regeneration and
          // storm the negotiation so it never completes (P0-1 trickle-ICE fix).
          if (
            peerConnection
            && typeof peerConnection.type === 'string'
            && peerConnection.type.startsWith('webrtc-')
          ) {
            return
          }

          // If a guest joined, generate WebRTC offer (WebRTC should already be initialized)
          if (who === 'peer' && this.webrtc && !peerConnection?.answer) {
            console.log('🔄 Guest joined - generating WebRTC offer')
            this.generateConnectionOffer()
          } else if (who === 'peer' && !this.webrtc) {
            console.warn('⚠️ Guest joined but WebRTC not initialized yet')
          } else if (who === 'peer' && peerConnection?.answer) {
            console.log('🔄 Guest rejoined - handling existing WebRTC connection')
            this.handlePeerJoined(who)
          }
        })

        // Presence: the guest's socket dropped. Show "reconnecting" right away
        // (faster than the WebRTC ICE timeout). Cleared when the guest rejoins and
        // the media path re-establishes (onConnectionStateChange → 'connected').
        this.socket.on('peer-left', (roomid, who) => {
          if (who === 'peer') {
            console.log('👋 Guest disconnected (socket) — reconnecting')
            this.guestConnected = false
            this.webrtcConnected = false
            this.guestReconnecting = true
            this.armReloadPrompt()
          }
        })

        // this.socket.on("msg", (roomid, who, content) => { //webrtc'ye devredilecek.
        //   console.log("Message received:", content);
        //   this.messages.push({
        //     id: Date.now(),
        //     from: who,
        //     content: content,
        //     timestamp: new Date(),
        //   });
        //   // Auto-scroll to bottom
        //   this.$nextTick(() => {
        //     if (this.$refs.chatMessages) {
        //       this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
        //     }
        //   });
        // });

        // P1-5 — apply whiteboard ops relayed from the guest. The host is
        // authoritative, so it never subscribes to `canvas-state` (which would
        // let an empty server snapshot wipe its own board).
        this.socket.on('canvas-op', (roomid, who, data) => {
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyCanvasUpdate(data)
          }
        })

        // P1-8 — render persisted chat history on join / reconnect, de-duplicated
        // by id so a message that also arrives live isn't shown twice.
        this.socket.on('chat-history', (roomid, history) => {
          const existing = new Set(this.messages.map(m => m.id));
          (history || []).forEach(msg => {
            const id = `${msg.ts}-${msg.who}`
            if (existing.has(id)) return
            existing.add(id)
            this.messages.push({
              id,
              from: msg.who === 'host' ? 'host' : 'guest',
              content: msg.text,
              timestamp: new Date(msg.ts),
            })
          })
          this.messages.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
          )
          this.scrollChatToBottom()
        })

        // P1-6 — server-relayed chat. Render every message from the socket so
        // there is a single source of truth for both peers (our own messages
        // arrive back here echoed by the server).
        this.socket.on('chat-msg', (roomid, msg) => {
          const mine = msg.who === 'host'
          this.messages.push({
            id: `${msg.ts}-${msg.who}`,
            from: mine ? 'host' : 'guest',
            content: msg.text,
            timestamp: new Date(msg.ts),
          })
          this.scrollChatToBottom()
        })

        this.socket.on('disconnect', () => {
          console.log('Disconnected from server')
          this.connected = false
          this.socketReissueCoordinator.onDisconnect()
        })

        // P2-9 — handshake rejected (the host's access token is missing/expired
        // before connecting; the socket does not auto-refresh it). Surface it
        // instead of leaving the host on a silent dead board. `connect_error`
        // ALSO fires on transient failures (server restart, network blip) that
        // socket.io recovers from on its own — only tear the session down when
        // the socket is inactive (a terminal handshake rejection), otherwise let
        // the built-in reconnection run.
        this.socket.on('connect_error', async err => {
          if (err && err.data && err.data.code === 'SESSION_REPLACED') {
            this.handleSessionReplacement(err.data)
            return
          }
          if (this.socket && this.socket.active) return // transient — will auto-retry
          // Terminal handshake rejection — most likely an access token that expired
          // during a long/idle session and was never refreshed. Try ONE silent token
          // refresh + reconnect before forcing re-login, so a browser refresh (or the
          // page just sitting open) recovers on its own.
          if (!this._triedTokenRefresh) {
            this._triedTokenRefresh = true
            try {
              const token = await useJwt.ensureFreshAccessToken(30)
              if (token) {
                this.socket.connect() // function-auth re-reads the fresh token
                return
              }
            } catch (e) {
              const data = e && e.response && e.response.data
              if (data && data.code === 'SESSION_REPLACED') {
                this.handleSessionReplacement(data)
                return
              }
              console.error('Token refresh during socket auth failed:', e.message)
            }
          }
          console.error('Host socket authentication failed:', err.message)
          this.handleAuthFailure()
        })

        // P2-9 — authenticated but not authorized for this room (not its host).
        this.socket.on('room-denied', () => {
          console.error('Access to this room was denied')
          this.handleAuthFailure()
        })
      } catch (error) {
        console.error('Socket connection failed:', error)
      }
    },

    // Initialize WebRTC for direct P2P communication
    async initializeWebRTC() {
      console.log('🚀 Initializing WebRTC as Host')

      try {
        // A socket reconnect re-runs createSession → this method; tear down the
        // previous service first so its socket listeners are removed (no stacking).
        if (this.webrtc) {
          try { this.webrtc.disconnect() } catch (e) { /* already gone */ }
        }
        this.webrtc = new WebRTCService()
        // Set up WebRTC event handlers

        this.webrtc.onCanvasUpdate = canvasData => {
          console.log('📥 Received canvas update via WebRTC')
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyCanvasUpdate(canvasData)
          }
        }

        // Hosts don't receive zoom updates, they control zoom
        this.webrtc.onZoomUpdate = null

        this.webrtc.onConnectionStateChange = state => {
          console.log('🔗 WebRTC connection state:', state)
          this.webrtcConnected = state === 'connected'
          this.guestConnected = state === 'connected'

          if (state === 'connected') {
            console.log('✅ WebRTC connection established - no manual setup needed')
            this.guestReconnecting = false
            this.disarmReloadPrompt()
            // Send initial canvas state and permissions when guest connects
            setTimeout(() => {
              this.sendInitialStateToGuest()
            }, 500)

            // Notify permission controls that a guest has joined
            if (this.$refs.permissionControls) {
              this.$refs.permissionControls.setGuestConnected(true)
            }
          } else if (state === 'disconnected' || state === 'failed') {
            if (this.$refs.permissionControls) {
              this.$refs.permissionControls.setGuestConnected(false)
            }
            console.log('❌ WebRTC connection lost/failed:', state)
            this.webrtcConnected = false
            this.guestConnected = false
            // Show "reconnecting" while the service auto-recovers (ICE-restart);
            // arm the reload fallback in case recovery doesn't land in time.
            this.guestReconnecting = true
            this.armReloadPrompt()
          }
        }

        this.webrtc.onMessage = message => {
          console.log('📥 Received message via WebRTC:', message)

          // Handle different message types
          if (typeof message === 'object' && message.type) {
            // Handle special message types if needed
            console.log('Received special message type:', message.type)
          } else {
            // Regular chat message from guest
            this.messages.push({
              id: Date.now(),
              from: 'guest',
              content: typeof message === 'string' ? message : message.content,
              timestamp: new Date(),
            })

            // Auto-scroll to bottom
            this.scrollChatToBottom()
          }
        }

        // Handle remote track addition (WebRTC ontrack)
        this.webrtc.onTrack = (stream, track) => {
          console.log('📥 Remote track added via WebRTC', track)
          this.remoteStream = stream
        }

        // Handle when connection offer is automatically sent via socket
        this.webrtc.onOfferReady = () => {
          console.log('📋 WebRTC offer automatically sent via socket')
          // No need to show manual connection data - everything is automatic now
        }

        this.webrtc.onRemoteStreamAdded = stream => {
          console.log('📥 Remote stream added via WebRTC')
          this.remoteStream = stream
          this.remoteVideoKey += 1 // Force re-render of GuestCamera component
        }

        // Handle when guest sends answer data
        this.webrtc.onAnswerReady = () => {
          console.log('📋 Received answer data from guest')
          // This won't be called for host, but keeping for completeness
        }

        // Fetch time-limited TURN/STUN credentials before init (P0-1).
        // P4-16 — retry on transient failures before falling back to STUN-only.
        const iceServers = await this.fetchTurnCredentials()
        if (iceServers) {
          this.webrtc.setIceServers(iceServers)
        } else {
          console.warn('Could not fetch TURN credentials, using default STUN')
        }

        // Initialize WebRTC as host
        const success = await this.webrtc.initializeAsHost(this.roomId, this.socket)
        if (success) {
          console.log('✅ WebRTC Host initialized successfully')
          console.log(
            '🔄 Ready to accept guest connections automatically via socket signaling',
          )
        }

        this.webrtc.remoteVideoState = state => {
          console.log('📹 Remote video state changed:', state)
          if (this.remoteStream) {
            this.remoteStream.getVideoTracks().forEach(track => {
              // Web MediaStreamTrack exposes enabled as its mutable control.
              // eslint-disable-next-line no-param-reassign
              track.enabled = state
            })
          }
        }

        this.webrtc.remoteAudioState = state => {
          console.log('🎤 Remote audio state changed:', state)
          // this.remoteStream?.getAudioTracks().forEach((track) => {
          //   track.enabled = state;
          // });
          this.remoteMuted = !state
        }
      } catch (error) {
        console.error('❌ Failed to initialize WebRTC:', error)
      }
    },

    // Send initial state to newly connected guest
    sendInitialStateToGuest() {
      if (!this.webrtc || !this.webrtcConnected) return

      // Send current canvas state
      if (this.$refs.canvasStage) {
        const currentState = this.$refs.canvasStage.getCurrentCanvasState()
        console.log('📤 Sending initial canvas state via WebRTC')
        this.webrtc.recreateChannels()
        this.webrtc.sendCanvasUpdate(currentState)

        // Send current zoom state
        const currentZoom = {
          scaleX: this.$refs.canvasStage.stageConfig.scaleX,
          scaleY: this.$refs.canvasStage.stageConfig.scaleY,
          x: this.$refs.canvasStage.stageConfig.x,
          y: this.$refs.canvasStage.stageConfig.y,
          timestamp: Date.now(),
        }
        console.log('📤 Sending initial zoom state via WebRTC:', currentZoom)
        this.webrtc.sendZoomUpdate(currentZoom)
      }

      // Send current permissions
      console.log('📤 Sending initial permissions via WebRTC')
      this.webrtc.sendPermissionUpdate(this.currentGuestPermissions)

      // P1-8 — chat history is now delivered by the server over `chat-history`
      // on join/reconnect, so the legacy WebRTC `initial_message` replay is
      // retired to avoid a double source of truth.
      this.webrtc.sendMicToggle(!this.micMuted)
    },

    async createSession() {
      try {
        // Get subject ID from route params (note: lowercase 'subjectid' in route)
        const subjectId = this.$route.params.subjectid
        if (!subjectId) {
          console.error('No subject ID provided in route')
          return
        }

        // Initialize LiveEntry data
        this.currentSubject = subjectId
        // Check if there's an exercise ID in route params or query (optional)
        const exerciseId = this.$route.params.exerciseid || this.$route.query.exerciseId
        if (exerciseId) {
          this.currentExerciseId = exerciseId
          this.showLiveEntry = true // Auto-show if exercise ID is provided
        }

        // kardesim biz zaten roomId'yi route'dan aliyoruz, session online'a tiklaninca geliyor amk.
        // Create session via API
        // const response = await this.$http.post(`/online/${subjectId}`);
        // this.roomId = response.data.uuid;

        // Join room as host
        this.socket.emit('peer-info', this.roomId, 'host', null)
        // P1-5 — join the Socket.IO room so we receive the guest's whiteboard
        // ops. The host is authoritative and does NOT apply `canvas-state`.
        this.socket.emit('join-room', this.roomId)
        console.log('Host joined room:', this.roomId)
        this.roomJoined = true

        // Initialize WebRTC immediately as host
        await this.initializeWebRTC()
        this.webrtcInitialized = true

        // Mark host as ready after a brief delay for UI smoothness
        setTimeout(() => {
          this.hostReady = true
        }, 400)
      } catch (error) {
        console.error('Failed to create session:', error)
        // Still show the screen even on error so user isn't stuck
        this.hostReady = true
      }
    },

    sendMessage() {
      const messageContent = this.newMessage.trim()
      if (messageContent && this.socket && this.roomId) {
        // P1-6 — send chat over Socket.IO instead of the WebRTC data channel.
        // The server echoes it back to everyone (incl. us), so we render on
        // receipt in the `chat-msg` handler — do NOT push locally here or the
        // message would appear twice.
        this.socket.emit('chat-msg', this.roomId, 'host', messageContent)
        this.newMessage = ''
      }
    },

    handlePeerJoined(who) {
      if (who === 'peer') {
        this.messages.push({
          id: Date.now(),
          from: 'system',
          content: 'Misafir katıldı',
          timestamp: new Date(),
        })

        // If WebRTC is already initialized, generate offer immediately
        // if (this.webrtc) {
        //   console.log("🔄 Guest joined - automatically generating WebRTC offer");
        //   this.generateConnectionOffer();
        // } else {
        //   console.log("⚠️ WebRTC not ready - will generate offer after initialization");
        // }
      }
    },

    // Generate connection offer for guest (now automatic via socket)
    async generateConnectionOffer() {
      if (!this.webrtc) {
        console.error('❌ WebRTC not initialized')
        return
      }

      console.log('🔗 Generating connection offer for guest...')
      // Rebuild the peer connection so a (re)joining guest negotiates from a clean
      // slate — fresh ICE/DTLS the new peer can actually match. This is what makes a
      // guest refresh restore mic/camera WITHOUT the host resending the link.
      // Re-attach local media before offering (the old tracks died with the old PC).
      this.webrtc.resetConnection()
      if (this.localStream) this.webrtc.addTracks(this.localStream)
      // The offer is sent via socket once ICE gathering completes.
      await this.webrtc.createOffer()
    },

    handleSessionClosed() {
      this.messages.push({
        id: Date.now(),
        from: 'system',
        content: 'Oturum kapatıldı',
        timestamp: new Date(),
      })
    },

    closeSession() {
      if (window.confirm('Bu oturumu kapatmak istediğinizden emin misiniz?')) {
        if (this.socket && this.roomId) {
          this.socket.emit('session-close', this.roomId)
        }
        this.$router.push('/online')
      }
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
          }
        }, 50)
      })
    },

    // Room ID methods
    copyRoomId() {
      if (this.roomId) {
        navigator.clipboard
          .writeText(this.roomId)
          .then(() => {
            console.log("Oda ID'si panoya kopyalandı!")
          })
          .catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = this.roomId
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            console.log('Room ID copied to clipboard!')
          })
      }
    },

    copyGuestUrl() {
      if (this.guestUrl) {
        navigator.clipboard
          .writeText(this.guestUrl)
          .then(() => {
            console.log('Guest URL copied to clipboard!')
            this.$swal({
              title: "Misafir URL'si Kopyalandı",
              icon: 'success',
              timer: 1000,
              showConfirmButton: false,
            })
          })
          .catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = this.guestUrl
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            console.log("Misafir URL'si panoya kopyalandı!")
          })
      }
    },
    // Terminate session method
    terminateSession() {
      if (window.confirm('Bu oturumu sonlandırmak istediğinizden emin misiniz?')) {
        console.log('🔴 Terminating session...')

        const terminationMessage = {
          type: 'session-terminated',
          redirectUrl: 'https://dikoturkiye.com/', // You can change this URL
        }

        // Send termination signal to guest via WebRTC
        if (this.webrtc && this.webrtcConnected) {
          console.log('📤 Sending termination via WebRTC:', terminationMessage)
          this.webrtc.sendMessage(terminationMessage)
        } else {
          console.log('⚠️ WebRTC not connected, cannot send termination message')
        }

        // Send termination signal via Socket as backup
        if (this.socket && this.connected) {
          console.log('📤 Sending termination via Socket')
          this.socket.emit('session-terminated', this.roomId, 'https://dikoturkiye.com/')
        } else {
          console.log('⚠️ Socket not connected, cannot send termination message')
        }

        // Clean up and close host tab after 2 seconds
        setTimeout(() => {
          console.log('🧹 Cleaning up connections...')
          this.cleanupStreams()
          if (this.webrtc) this.webrtc.disconnect()
          if (this.socket) this.socket.disconnect()

          // Try multiple ways to close the window
          try {
            // First try window.close()
            console.log('🚪 Attempting to close window...')
            window.close()

            // If window.close() doesn't work (common in modern browsers), redirect
            setTimeout(() => {
              console.log('🔄 Window close failed, redirecting...')
              window.location.href = '/online'
            }, 1000)
          } catch (error) {
            console.log('❌ Error closing window:', error)
            // Fallback: redirect to online page
            window.location.href = '/online'
          }
        }, 2000)
      }
    },
  },
}
</script>
<style src="./OnlineViewStyle.css" scoped></style>
<style scoped>
/* Host-specific overrides can go here if needed */

.session-reauth-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(25, 19, 23, 0.9);
  backdrop-filter: blur(14px);
}

.session-reauth-card {
  width: min(420px, 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 36px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.35);
  text-align: center;
}

.session-reauth-icon {
  color: #de39a5;
  font-size: 34px;
}

.session-reauth-card h2 {
  margin: 0;
  color: #2a1e25;
  font-size: 22px;
}

.session-reauth-card p {
  margin: 0 0 6px;
  color: #6f6670;
  line-height: 1.5;
}

.session-reauth-card input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d8d1d6;
  border-radius: 10px;
  outline: none;
}

.session-reauth-card input:focus {
  border-color: #de39a5;
  box-shadow: 0 0 0 3px rgba(222, 57, 165, 0.12);
}

.session-reauth-card button {
  padding: 12px 16px;
  border: 0;
  border-radius: 10px;
  background: #de39a5;
  color: #fff;
  font-weight: 700;
}

.session-reauth-card button:disabled {
  opacity: 0.65;
}

.session-reauth-card .session-reauth-cancel {
  background: transparent;
  color: #6f6670;
}

.session-reauth-error {
  color: #c9304c;
  font-size: 13px;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #2a1e25 0%, #1e1e1e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-card {
  text-align: center;
  padding: 48px 40px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
}

.spinner-ring {
  position: absolute;
  inset: 0;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #de39a5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  color: #de39a5;
}

.loading-title {
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: 0.5px;
}

.loading-subtitle {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0 0 28px;
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.loading-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  transition: all 0.4s ease;
}

.loading-step i {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.3);
  min-width: 18px;
  text-align: center;
  transition: color 0.4s ease;
}

.loading-step span {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  font-weight: 500;
  transition: color 0.4s ease;
}

.loading-step.done {
  background: rgba(40, 199, 111, 0.1); /* #28c76f with opacity */
}

.loading-step.done i {
  color: #28c76f;
}

.loading-step.done span {
  color: rgba(255, 255, 255, 0.85);
}

/* Fade transition for loading overlay */
.fade-loading-enter-active,
.fade-loading-leave-active {
  transition: opacity 0.5s ease;
}

.fade-loading-enter,
.fade-loading-leave-to {
  opacity: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Room ID Display */
.room-info {
  margin-bottom: 16px;
}

.room-id-container {
  background-color: #f8f9fa;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 12px;
}

.room-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.room-header h5 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.copy-btn,
.copy-url-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.copy-btn:hover,
.copy-url-btn:hover {
  background-color: #e9ecef;
}

.room-id-display {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}

.room-id-display code {
  font-family: "Courier New", monospace;
  font-size: 12px;
  color: #495057;
  word-break: break-all;
}

.guest-url {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background-color: #e7f3ff;
  border-radius: 4px;
  border: 1px solid #b3d7ff;
}

.guest-url small {
  font-size: 10px;
  color: #0066cc;
  word-break: break-all;
  margin-right: 8px;
  flex: 1;
}

/* Chat styles */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 300px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #e1e5e9;
  background-color: #f8f9fa;
}

.chat-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #dc3545;
}

.connection-status.connected {
  color: #28a745;
}

.connection-status i {
  font-size: 8px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  background-color: #fff;
}

.message {
  margin-bottom: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  max-width: 80%;
}

.message.own {
  background-color: #007bff;
  color: white;
  margin-left: auto;
  text-align: right;
}

.message:not(.own):not(.system) {
  background-color: #e9ecef;
  color: #333;
}

.message.system {
  background-color: #fff3cd;
  color: #856404;
  text-align: center;
  font-style: italic;
  font-size: 12px;
  margin-left: auto;
  margin-right: auto;
}

.message-content {
  font-size: 13px;
  line-height: 1.4;
}

.message-time {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 2px;
}

.chat-input {
  display: flex;
  padding: 8px;
  border-top: 1px solid #e1e5e9;
  background-color: #f8f9fa;
}

.message-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  width: 100%;
}

.message-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.send-btn {
  margin-left: 8px;
  padding: 6px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.send-btn:hover {
  background-color: #0056b3;
}

/* Automatic WebRTC Connection Status */
.connection-status .connection-container {
  background-color: #f0f8ff;
  border: 1px solid #b3d7ff;
  border-radius: 8px;
  padding: 12px;
}

.connection-status .connection-header {
  margin-bottom: 8px;
}

.connection-status .connection-header h5 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #0066cc;
}

.connection-status .connection-info {
  text-align: center;
}

.connection-status .connection-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #dc3545;
}

.connection-status .connection-status.connected {
  color: #28a745;
}

.connection-status .connection-status i {
  font-size: 8px;
}

/* LiveEntry section styling */
.live-entry-section {
  margin-top: 16px;
  border-top: 1px solid #e1e5e9;
  padding-top: 16px;
}

.live-entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e1e5e9;
}

.live-entry-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.close-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;
}

.close-btn:hover {
  background-color: #e9ecef;
  color: #495057;
}

.live-entry-toggle {
  width: 100%;
  padding: 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  transition: background-color 0.2s;
}

.live-entry-toggle:hover {
  background-color: #0056b3;
}

.live-entry-section .entry-card {
  margin-bottom: 12px;
  border: 1px solid #e1e5e9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.live-entry-section .nav-card {
  border: 1px solid #e1e5e9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.maximize {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive adjustments for LiveEntry in sidebar */
@media (max-width: 768px) {
  .live-entry-section .entry-card,
  .live-entry-section .nav-card {
    min-width: unset;
    max-width: unset;
  }

  .live-entry-section .entry-button {
    font-size: 1rem;
    height: 40px;
  }

  .live-entry-section .custom-nav-button {
    font-size: 1rem;
    height: 40px;
  }

  .live-entry-toggle {
    padding: 10px;
    font-size: 13px;
  }
}
.permission-status {
  margin-bottom: 10px;
  font-size: 12px;
}

.device-status {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.audio-status,
.video-status {
  display: flex;
  align-items: center;
  gap: 5px;
}

.no-devices {
  color: #dc3545;
  text-align: center;
  padding: 10px;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}
</style>
